import { useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';

import { useAllowedApps } from '@verantos-dev/core-auth';
import {
  getCohortsPath,
  getDataAdminDataDeliveryPath,
  getOrganizationsBasePath,
  getProjectAccuracyPath,
  getProjectsBasePath,
  getUsersBasePath,
  getConceptGroupsBasePath,
} from '@verantos-dev/core-constants';
import {
  DataAdministrationSidebarAppFeaturesId,
  EvidencePlatformSidebarAppID,
  OrganizationsSidebarAppFeatureId,
  ProjectsSidebarAppFeatureId,
  UserSidebarAppFeatureId,
} from '@verantos-dev/core-enums';
import type { IconType, SidebarItemDetail } from '@verantos-dev/shared-components';

export const useSidebarItems = () => {
  const navigate = useNavigate();
  let { projectId } = useParams();

  const intl = useIntl();

  const sidebarApps = useAllowedApps();
  // Check for first pathname
  const path = window.location.pathname;
  const segments = path.replace(/\/+$/, '').split('/').filter(Boolean);

  // We are fetching projectId from the URL if it's not available through react router params
  if (!projectId) {
    const projectsIndex = segments.indexOf('projects');
    projectId = projectsIndex !== -1 ? segments[projectsIndex + 1] : '';
  }

  // get top level sidebar app
  let sidebarItems: SidebarItemDetail[] | undefined = undefined;

  sidebarItems = sidebarApps?.reduce((_sidebarItems, app) => {
    const nextSidebarItem = {
      id: app.appId,
      label: intl.formatMessage({ id: `sidebar.${app.appName}` }),
      icon: getIconForAppId(app.appId),
      selected: matchPatternsForSidebarAppId(app.appId).includes(segments[segments.length - 1]),
      disabled: false,
      isSelectable: true,
      onClick: getOnClickHandlerForAppId(app.appId, navigate),
      secondaryLabel: '',
      secondaryIcon: '',
      divider: false,
    };

    let hasActiveSubItemForApp = false;
    const subItems: SidebarItemDetail[] | undefined = app.features?.map((feature, index) => {
      const isSelectable = matchPatternsForSidebarAppId(feature.featureId).includes(
        segments[segments.length - 1]
      );
      hasActiveSubItemForApp = hasActiveSubItemForApp || isSelectable;
      return {
        id: feature.featureId as string,
        label: intl.formatMessage({ id: `sidebar.${app.appName}.${feature.featureId}` }),
        icon: getIconForAppId(feature.featureId),
        selected: isSelectable,
        disabled: false,
        isSelectable: true,
        onClick: getOnClickHandlerForAppId(feature.featureId, navigate, projectId),
        divider: false,
        secondaryLabel: '',
        secondaryIcon: '',
      };
    });

    const additionalItems: SidebarItemDetail[] | undefined = app.additionalItems?.map(
      (feature, index) => {
        const isSelectable = matchPatternsForSidebarAppId(feature.featureId).includes(
          segments[segments.length - 1]
        );
        return {
          id: feature.featureId as string,
          label: intl.formatMessage({ id: `sidebar.${feature.featureId}` }),
          icon: getIconForAppId(feature.featureId),
          selected: isSelectable,
          disabled: false,
          isSelectable: true,
          onClick: getOnClickHandlerForAppId(feature.featureId, navigate),
          divider: false,
          secondaryLabel: '',
          secondaryIcon: '',
        };
      }
    );

    const sectionTitle: SidebarItemDetail | undefined = app?.sectionTitleId
      ? {
          icon: 'noIcon',
          id: app.appId + 'section',
          isSelectable: false,
          label: '',
          secondaryLabel: intl.formatMessage({
            id: `sidebar.sectionTitle.${app.sectionTitleId}`,
          }),
          sectionLabel: true,
        }
      : undefined;

    if (hasActiveSubItemForApp) {
      subItems[subItems.length - 1].divider = additionalItems.length === 0;
      if (additionalItems.length > 0) {
        additionalItems[additionalItems.length - 1].divider = true;
      } else if (subItems.length > 0) {
        subItems[subItems.length - 1].divider = true;
      } else {
        nextSidebarItem.divider = true;
      }

      return sectionTitle
        ? [..._sidebarItems, sectionTitle, nextSidebarItem, ...additionalItems, ...subItems]
        : [..._sidebarItems, nextSidebarItem, ...additionalItems, ...subItems];
    }

    if (additionalItems?.length > 0) {
      additionalItems[additionalItems.length - 1].divider = true;
    } else {
      nextSidebarItem.divider = true;
    }
    return sectionTitle
      ? [..._sidebarItems, sectionTitle, nextSidebarItem, ...additionalItems]
      : [..._sidebarItems, nextSidebarItem, ...(additionalItems || [])];
  }, [] as SidebarItemDetail[]);
  return { sidebarItems };
};

const getOnClickHandlerForAppId = (
  appId:
    | EvidencePlatformSidebarAppID
    | UserSidebarAppFeatureId
    | ProjectsSidebarAppFeatureId
    | DataAdministrationSidebarAppFeaturesId
    | OrganizationsSidebarAppFeatureId,
  navigate: ReturnType<typeof useNavigate>,
  projectId?: string
) => {
  const cleanupCohortState = () => {
    // dispatch(resetCohortBuilderSlice());
    // dispatch(resetResearchAssistantMessages());
  };
  const cleanupStudyState = () => {
    cleanupCohortState();
    // dispatch(resetCurrentState());
  };

  switch (appId) {
    case EvidencePlatformSidebarAppID.projects:
    case EvidencePlatformSidebarAppID.conceptGroups:
      return () => {
        cleanupStudyState();
        navigate(getUrlForSidebarAppId(appId));
      };
    case EvidencePlatformSidebarAppID.users:
    case EvidencePlatformSidebarAppID.organizations:
    case EvidencePlatformSidebarAppID.dataAdministration:
    case EvidencePlatformSidebarAppID.extractionAccuracy:
    case DataAdministrationSidebarAppFeaturesId.dataIntake:
    case DataAdministrationSidebarAppFeaturesId.datasetDelivery:
      return () => {
        navigate(getUrlForSidebarAppId(appId));
      };
    case ProjectsSidebarAppFeatureId.cohorts:
      return () => {
        cleanupCohortState();
        navigate(getUrlForSidebarAppId(appId, projectId));
      };
    case ProjectsSidebarAppFeatureId.dataReliability:
      return () => {
        navigate(getUrlForSidebarAppId(appId, projectId));
      };
    default:
      return () => {
        navigate('/');
      };
  }
};

const getActiveItemForAppId = (
  appId:
    | EvidencePlatformSidebarAppID
    | UserSidebarAppFeatureId
    | ProjectsSidebarAppFeatureId
    | DataAdministrationSidebarAppFeaturesId
    | OrganizationsSidebarAppFeatureId,
  activeStudyName?: string
): SidebarItemDetail | undefined => {
  if (appId === EvidencePlatformSidebarAppID.projects) {
    return {
      id: 'studyName',
      label: '',
      secondaryLabel: `${activeStudyName}`,
      icon: 'noIcon',
      isSelectable: false,
    };
  }

  return undefined;
};

const getIconForAppId = (
  appId:
    | EvidencePlatformSidebarAppID
    | UserSidebarAppFeatureId
    | ProjectsSidebarAppFeatureId
    | DataAdministrationSidebarAppFeaturesId
    | OrganizationsSidebarAppFeatureId
): 'noIcon' | ('blank' | IconType) => {
  switch (appId) {
    case EvidencePlatformSidebarAppID.projects:
      return 'sidebarStudies';
    case EvidencePlatformSidebarAppID.users:
      return 'sidebarUsers';
    case EvidencePlatformSidebarAppID.organizations:
      return 'sidebarOrganization';
    case EvidencePlatformSidebarAppID.dataAdministration:
      return 'sidebarDataAdmin';
    case EvidencePlatformSidebarAppID.extractionAccuracy:
      return 'sidebarExtractionAccuracy';
    case EvidencePlatformSidebarAppID.conceptGroups:
      return 'sidebarConceptGroups';
    case DataAdministrationSidebarAppFeaturesId.dataIntake:
      return 'sidebarDataAdmin';
    case DataAdministrationSidebarAppFeaturesId.datasetDelivery:
      return 'sidebarDataAdmin';
    case ProjectsSidebarAppFeatureId.cohorts:
      return 'sidebarCohorts';
    case ProjectsSidebarAppFeatureId.dataReliability:
      return 'sidebarAccuracy';
    default:
      return 'noIcon';
  }
};

const matchPatternsForSidebarAppId = (
  appId:
    | EvidencePlatformSidebarAppID
    | UserSidebarAppFeatureId
    | ProjectsSidebarAppFeatureId
    | DataAdministrationSidebarAppFeaturesId
    | OrganizationsSidebarAppFeatureId
): string[] => {
  switch (appId) {
    case EvidencePlatformSidebarAppID.projects:
    case EvidencePlatformSidebarAppID.users:
    case EvidencePlatformSidebarAppID.conceptGroups:
      return [appId];
    case ProjectsSidebarAppFeatureId.cohorts:
      return [appId, 'cohort-builder'];
    case EvidencePlatformSidebarAppID.organizations:
      return [appId, 'customers', 'research-partners', 'verantos'];
    case ProjectsSidebarAppFeatureId.dataReliability:
      return [appId, 'accuracy', 'completeness', 'traceability'];
    default:
      return ['/'];
  }
};

export const getUrlForSidebarAppId = (
  appId:
    | EvidencePlatformSidebarAppID
    | UserSidebarAppFeatureId
    | ProjectsSidebarAppFeatureId
    | DataAdministrationSidebarAppFeaturesId
    | OrganizationsSidebarAppFeatureId,
  projectId?: string
) => {
  switch (appId) {
    case EvidencePlatformSidebarAppID.projects:
      return getProjectsBasePath();
    case EvidencePlatformSidebarAppID.users:
      return getUsersBasePath();
    case EvidencePlatformSidebarAppID.organizations:
      return getOrganizationsBasePath();
    case EvidencePlatformSidebarAppID.dataAdministration:
      return getDataAdminDataDeliveryPath();
    case EvidencePlatformSidebarAppID.conceptGroups:
      return getConceptGroupsBasePath();
    case ProjectsSidebarAppFeatureId.cohorts:
      return getCohortsPath(projectId || '');
    case ProjectsSidebarAppFeatureId.dataReliability:
      return getProjectAccuracyPath(projectId || '');
    default:
      return '/';
  }
};
